import { Box, Typography } from "@mui/material";
import { NotificationsPlugin } from "@pimo/notifications-plugin-v2";
import type {
  App,
  Plugin,
  Route,
  UniversalAppState,
  View,
} from "@pimo/pimo-app-builder";
import {
  Chip,
  DefaultOverlayPartialAppState,
  PMButton,
  PMPopUp,
} from "@pimo/pimo-components";
import { DialogLayout } from "@pimo/pimo-components/src/lib/layouts";
import { getLocalizedMessage, type SupportedLanguage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  Profile,
  Project,
  SettingsCommit,
  SettingsCommitCustomFieldUpsert,
  SettingsCommitUserChange,
  SettingsResponse,
  SettingsUsersResponse,
} from "product-test-types";
import {
  AUDIT_VIEW_CONFIG,
  MATURITY_VIEW_CONFIG,
  PMO_VIEW_CONFIG,
  ROLLOUT_VIEW_CONFIG,
  validateConfiguration,
  VIEW_NAMES,
} from "product-test-utils";

// import ReactCountryFlag from "react-country-flag";
import { TestAppState } from "../app";
import { AntSwitch } from "../components/components/ant-switch";
import { ItemBasicFieldsForm } from "../components/define-items-data-model/items-basic-fields-form";
import { ProjectsBasicFieldsForm } from "../components/define-projects-data-model/projects-basic-fields-form";
import { ReportingFieldsForm } from "../components/define-reporting-data/reporting-data-fields-form";
import { ExcelImportPopup } from "../components/excel-import-popup";
import { ItemManagerPage } from "../components/manage-items/manage-subentities";
import { ManageOEsSettingsPage } from "../components/manage-projects/manage-main-entities-page";
import { ManageUsersPage } from "../components/manage-users/manage-users";
import { ReportingPeriodPage } from "../components/reporting-period";
import { ReportingStructurePage } from "../components/reporting-structure";
import { ReportingUseCasePage } from "../components/reporting-use-case";
import { APP_ROUTES } from "../constants";
import { importItems, parseExcelHeaders } from "../helpers/excel-importer";
import { fetchDashboard } from "../helpers/fetch-dashboard";
import { fetchOverlay } from "../helpers/fetch-overlay";
import {
  clearSettings,
  commitSettings,
  fetchSettings,
} from "../helpers/fetch-settings";
import { saveUserSettings } from "../helpers/fetch-user-settings";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
import {
  getItemName,
  getPluralOfItemName,
} from "../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../utils/get-plural-of-project-name";
// eslint-disable-next-line
export interface SettingsPluginState {}

export type SettingsViewName = (typeof VIEW_NAMES)[number];

/**
 * Maps a `/bff-settings/users` fetch result onto the tool/client user fields
 * of `editSettingsForm`. `fetchUsers` returns `undefined` when the request
 * failed (network error, or a 500 such as a Keycloak MFA-detail lookup
 * failure) — that must stay `undefined` here, not collapse to `[]`.
 *
 * `saveSettings`/`commit` on the backend treat an explicit empty tool-user
 * array as "the admin wants zero tool users" and sweep every other user's
 * group membership for this tool/client. Coercing a failed fetch to `[]`
 * would make the very next publish look like that explicit request and mass
 * deprovision every real user. Leaving the fields `undefined` preserves the
 * "no explicit change" semantics the backend already relies on.
 */
export function buildUsersLoadStatePatch(
  users: SettingsUsersResponse | undefined
): Pick<
  NonNullable<TestAppState["editSettingsForm"]>,
  "toolUsers" | "clientUsers"
> {
  return {
    toolUsers: users?.toolUsers,
    clientUsers: users?.clientUsers,
  };
}

export function buildLanguageSwitchStatePatch(
  currentState: Pick<TestAppState, "settings" | "editSettingsForm">,
  freshSettings: SettingsResponse
): Pick<TestAppState, "settings" | "editSettingsForm"> {
  const currentForm = currentState.editSettingsForm;

  return {
    settings: {
      projects: freshSettings.projects,
      basicItems: freshSettings.basicItems ?? [],
      configuration: freshSettings.configuration,
      mfaPolicy: freshSettings.mfaPolicy ?? currentState.settings?.mfaPolicy,
    },
    editSettingsForm: {
      ...currentForm,
      projects: freshSettings.projects,
      basicItems: freshSettings.basicItems ?? [],
      configuration: freshSettings.configuration,
      toolUsers: currentForm?.toolUsers ?? freshSettings.toolUsers ?? [],
      clientUsers: currentForm?.clientUsers ?? freshSettings.clientUsers ?? [],
      seatInfo: freshSettings.seatInfo ?? currentForm?.seatInfo,
      mfaPolicy: freshSettings.mfaPolicy ?? currentForm?.mfaPolicy,
    },
  };
}

export class SettingsPlugin implements Plugin<
  TestAppState,
  SettingsPluginState,
  "viewname"
> {
  route?: Route<"viewname", TestAppState>;
  private settingsView?: View<TestAppState, DialogLayout>;
  private app?: App<TestAppState>;

  async refreshSettingsForLanguage(language: SupportedLanguage) {
    const currentState = this.app?.getAppState();
    if (!currentState) {
      return;
    }

    const freshSettings = await fetchSettings(language);
    this.app?.patchAppState(
      buildLanguageSwitchStatePatch(currentState, freshSettings)
    );
  }

  onRegister(app: App<TestAppState>): void {
    this.app = app;

    this.settingsView = app.createView({
      name: "Settings",
      layout: new DialogLayout(),
    });

    this.buildRoute();
    this.buildReportingStructureView();
    this.buildReportingPeriodView();
    this.buildProjectsDataModelView();
    this.buildItemsDataModelView();
    this.buildManageReprtingDataView();
    this.buildManageProjectsView();
    this.buildManageActivitiesView();
    this.buildManageUsersView();
    this.buildExcelMappingDialog();
    this.setupExcelImportHandlers();
    this.buildNotificationsView();

    this.buildReportingUseCaseView();

    this.settingsView?.mapLayoutState((state) => {
      const resourceLockPlugin =
        this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );
      const language = state.language;
      const configuration = state.settings?.configuration;

      const projectName = {
        single: getProjectName(configuration?.projectName, language),
        plural: getPluralOfProjectName(configuration?.projectName, language),
      };
      const itemName = {
        single: getItemName(configuration?.itemName, language),
        plural: getPluralOfItemName(configuration?.itemName, language),
      };

      const completedWelcomeSteps = state.welcomeScreenStatus?.steps ?? {
        hasGeneralData: false,
        hasReportingData: false,
        hasProjectsAndDeliverables: false,
        hasInvitedUsers: false,
        hasNotifications: false,
      };

      const hasUnpublishedChanges =
        !state?.editSettingsForm?.configuration?.isPublished;
      const hasCreatedFields = state.editSettingsForm?.hasCreatedFields;
      const hasDeletedFields = state.editSettingsForm?.hasDeletedFields;

      const entries = [
        {
          title:
            getLocalizedMessage(language, "settingsPage.generalData") ||
            "General Data",
          icon: "CheckCircleOutline",
          iconProps: {
            color: completedWelcomeSteps?.hasGeneralData
              ? theme.palette.success.main
              : theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.reportingStructure,
              title:
                getLocalizedMessage(
                  language,
                  "settingsPage.reportingStructure"
                ) || "Reporting Structure",
              viewname: VIEW_NAMES[0],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[0])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[0])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.reportingPeriod,
              title:
                getLocalizedMessage(language, "settingsPage.reportingPeriod") ||
                "Reporting Period",
              viewname: VIEW_NAMES[1],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[1])
                ? "Error"
                : undefined,

              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[1])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(language, "settingsPage.dataModel") ||
            "Data Model",
          icon: "CheckCircleOutline",
          iconProps: {
            color: completedWelcomeSteps?.hasReportingData
              ? theme.palette.success.main
              : theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageProjectsDataModel,
              title: getLocalizedMessage(
                language,
                "dataModel.masterDataProject",
                { projectName: projectName.plural }
              ),

              viewname: VIEW_NAMES[2],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[2])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[2])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageItemsDataModel,
              title: getLocalizedMessage(language, "dataModel.masterDataItem", {
                itemName: itemName.plural,
              }),
              viewname: VIEW_NAMES[3],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[3])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[3])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageReportingData,
              title:
                getLocalizedMessage(language, "settingsPage.reporting") ||
                "Reporting",
              viewname: VIEW_NAMES[4],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[4])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[4])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(language, "settingsPage.content") || "Content",
          icon: "CheckCircleOutline",
          iconProps: {
            color: completedWelcomeSteps?.hasProjectsAndDeliverables
              ? theme.palette.success.main
              : theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageMainEntities,
              title: getLocalizedMessage(
                language,
                "settingsPage.manageProjects",
                { projectName: projectName.plural }
              ),
              viewname: VIEW_NAMES[5],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[5])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[5])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageSubentities,

              title: getLocalizedMessage(language, "settingsPage.manageItems", {
                itemName: itemName.plural,
              }),
              viewname: VIEW_NAMES[6],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[6])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[6])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(language, "manageUsers.title") ||
            "User Management",
          icon: "CheckCircleOutline",
          iconProps: {
            color: completedWelcomeSteps?.hasInvitedUsers
              ? theme.palette.success.main
              : theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageUsers,
              title:
                getLocalizedMessage(language, "manageUsers.title") ||
                "Manage Access",
              viewname: VIEW_NAMES[7],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[7])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[7])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },

        {
          title: getLocalizedMessage(language, "setupSteps.notificationsTitle"),
          icon: "CheckCircleOutline",
          iconProps: {
            color: completedWelcomeSteps?.hasNotifications
              ? theme.palette.success.main
              : theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.notifications,
              title: getLocalizedMessage(
                language,
                "settingsPage.notificationManager"
              ),
              viewname: VIEW_NAMES[8],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[8])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[8])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },

        /**
         * this entry should only be shown for the devs to help them develop,
         * in this way we enable it but by using feature flags we hide it from the production environment
         */
        ...(__ENV__ === "development"
          ? [
              {
                title:
                  getLocalizedMessage(language, "settingsPage.generalData") ||
                  "For testing",
                level: 0,
                items: [
                  {
                    link: APP_ROUTES.settings.reportingUseCase,
                    title:
                      getLocalizedMessage(language, "reportingUseCase.title") ||
                      "Reporting Use Case",
                    viewname: VIEW_NAMES[9],
                    level: 1,
                    icon: Object.entries(
                      state.editSettingsForm?.validationErrors ?? {}
                    ).find(
                      ([, err]) => err?.tabContainingErrors === VIEW_NAMES[9]
                    )
                      ? "Error"
                      : undefined,
                    iconProps: Object.entries(
                      state.editSettingsForm?.validationErrors ?? {}
                    ).find(
                      ([, err]) => err?.tabContainingErrors === VIEW_NAMES[9]
                    )
                      ? {
                          color: theme.palette.error.main,
                        }
                      : undefined,
                  },
                ],
              },
            ]
          : []),
      ];

      const useCaseName = state.settings?.configuration?.useCase
        ? getLocalizedMessage(
            language,
            `useCase.${state.settings?.configuration?.useCase}`
          )
        : undefined;

      return {
        entries,
        title: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {getLocalizedMessage(language, "settingsPage.reportingSettings") ||
              "Reporting Settings"}
            {useCaseName && (
              <Chip
                sx={{
                  backgroundColor: "#EBE3F4",
                  fontWeight: 500,
                  fontSize: "12px",
                }}
              >
                {`Use Case: ${useCaseName}`}
              </Chip>
            )}
          </Box>
        ),
        icon: {
          alt: "Settings",
          small: "pimo-logo.svg",
          big: "pimo-logo.svg",
        },
        nodes: [
          <Box
            key="lang-save-row"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            {/* Language selector hidden - default language is English */}
            {/* Language selector intentionally hidden while English remains the
                only supported settings-edit language. If it is re-enabled,
                call refreshSettingsForLanguage() so toolUsers/clientUsers and
                their MFA status chips stay intact. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">
                {getLocalizedMessage(language, "settingsPage.activateReport")}
              </Typography>
              <AntSwitch
                sx={{ marginLeft: 1 }}
                checked={
                  state.editSettingsForm?.configuration?.isReportingEnabled ??
                  false
                }
                onChange={(e) => {
                  this.app?.patchAppState({
                    editSettingsForm: {
                      ...state.editSettingsForm,
                      configuration: {
                        ...state.editSettingsForm?.configuration,
                        isReportingEnabled: e.target.checked,
                      } as Configuration,
                      enableSaveButton: true,
                    },
                  });
                }}
              />
            </Box>
            <PMButton
              loading={state.editSettingsForm?.saveInProgress}
              variant={
                state.editSettingsForm?.enableSaveButton
                  ? "contained"
                  : "disabled"
              }
              size="medium"
              text={getLocalizedMessage(language, "saveButton") || "Save"}
              onClick={() => {
                this.saveSettings(false);
              }}
            />
            <PMButton
              loading={state.editSettingsForm?.saveInProgress}
              variant={
                state.editSettingsForm?.configuration?.isPublished
                  ? "disabled"
                  : "report"
              }
              size="medium"
              text={"Publish"}
              onClick={() => {
                if (!this.app) return;
                const state = this.app.getAppState();

                const skipChangedFieldDialog =
                  state.userSettings?.skipChangedFieldDialog ?? false;
                const hasCreatedFields =
                  state.editSettingsForm?.hasCreatedFields;
                const hasDeletedFields =
                  state.editSettingsForm?.hasDeletedFields;

                if (
                  !skipChangedFieldDialog &&
                  (hasCreatedFields || hasDeletedFields)
                ) {
                  this.app?.patchAppState({
                    showModifiedFieldsDialog: true,
                  });
                  return;
                }

                this.saveSettings(true);
              }}
            />
            <PMButton
              loading={state.editSettingsForm?.saveInProgress}
              variant="contained"
              size="medium"
              text={getLocalizedMessage(language, "clearAll") ?? "Clear All"}
              onClick={async () => {
                this.app?.patchAppState({
                  showClearAllDataDialog: true,
                });
              }}
            />

            {/* Clear All Data Dialog */}
            <PMPopUp
              open={!!state.showClearAllDataDialog}
              title={
                getLocalizedMessage(
                  language,
                  "dialog.title.clearAllSettings"
                ) || "Clear All Data"
              }
              description={
                getLocalizedMessage(
                  language,
                  "dialog.message.clearAllSettings"
                ) ||
                "Are you sure you want to clear all settings data? This action cannot be undone."
              }
              onClose={() => {
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                });
              }}
              maxWidth="sm"
              cancelButtonColor="white"
              cancelButtonText={
                getLocalizedMessage(language, "dialog.button.cancel") ||
                "Cancel"
              }
              cancelButtonFireEvent={() => {
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                });
              }}
              confirmationButtonText={
                getLocalizedMessage(language, "clearAll") || "Clear All"
              }
              confirmationButtonColor="blue"
              confirmationButtonFireEvent={async () => {
                const [settings, overlay, dashboard] = await Promise.all([
                  clearSettings({
                    users: state.editSettingsForm?.users ?? [],
                  }),
                  fetchOverlay(),
                  fetchDashboard(),
                ]);

                showSnackbar(
                  this.app as unknown as App<
                    UniversalAppState & DefaultOverlayPartialAppState
                  >,
                  getLocalizedMessage(language, "settings.clearAllSuccess") ||
                    "All settings data have been cleared.",
                  "success"
                );
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                  overlay,
                  dashboard,
                  settings: {
                    projects: settings?.projects ?? ([] as Project[]),
                    basicItems: settings?.basicItems ?? ([] as BasicItem[]),
                    configuration:
                      settings?.configuration ?? ({} as Configuration),
                    mfaPolicy: state.editSettingsForm?.mfaPolicy,
                  },
                  editSettingsForm: {
                    projects: settings?.projects ?? ([] as Project[]),
                    basicItems: settings?.basicItems ?? ([] as BasicItem[]),
                    configuration:
                      settings?.configuration ?? ({} as Configuration),
                    seatInfo: state.editSettingsForm?.seatInfo,
                    mfaPolicy: state.editSettingsForm?.mfaPolicy,
                  },
                });
              }}
            />
            {/* Unsaved/Unpublished changes dialog */}
            <PMPopUp
              open={!!state.showUnsavedSettingsDialog}
              title={
                hasUnpublishedChanges
                  ? getLocalizedMessage(
                      language,
                      "dialog.title.unpublishedChanges"
                    ) || "Unpublished Changes"
                  : getLocalizedMessage(
                      language,
                      "dialog.title.unsavedChanges"
                    ) || "Unsaved Changes"
              }
              description={
                hasUnpublishedChanges
                  ? getLocalizedMessage(
                      language,
                      "dialog.message.unpublishedChanges",
                      { entity: "settings" }
                    ) ||
                    "You have made changes that have been saved but not published. If you close the settings, the changes will not be displayed in the workspace."
                  : getLocalizedMessage(
                      language,
                      "dialog.message.unsavedChanges",
                      { entity: "settings" }
                    ) ||
                    "You have made changes that have not been saved. If you close the settings, these changes will be lost."
              }
              onClose={() =>
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                })
              }
              maxWidth="sm"
              cancelButtonText={
                getLocalizedMessage(language, "dialog.button.cancel") ||
                "Cancel"
              }
              cancelButtonFireEvent={() =>
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                })
              }
              cancelButtonColor="white"
              confirmationButtonColor="blue"
              confirmationButtonText={
                getLocalizedMessage(language, "dialog.button.continue") ||
                "Continue"
              }
              confirmationButtonFireEvent={async () => {
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                });

                this.app?.navigate(APP_ROUTES.dashboard);
                resourceLockPlugin?.unlock("settings");
                const [overlay] = await Promise.all([fetchOverlay()]);

                this.app?.patchAppState({
                  overlay,
                });
              }}
            />
            {/* Modified Fields Dialog */}
            <PMPopUp
              open={!!state.showModifiedFieldsDialog}
              title={this.getModifiedFieldsDialogTitle(
                hasCreatedFields,
                hasDeletedFields
              )}
              description={this.getModifiedFieldsDialogText(
                hasCreatedFields,
                hasDeletedFields
              )}
              onClose={() =>
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                })
              }
              maxWidth="sm"
              cancelButtonText={
                getLocalizedMessage(language, "dialog.button.cancel") ||
                "Cancel"
              }
              cancelButtonFireEvent={() =>
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                })
              }
              cancelButtonColor="white"
              confirmationButtonColor="blue"
              confirmationButtonText={
                getLocalizedMessage(language, "dialog.button.continue") ||
                "Continue"
              }
              customCheckboxText={
                getLocalizedMessage(language, "dialog.message.dontShowAgain") ||
                "Don't ask me again"
              }
              confirmationButtonFireEvent={async (_, data) => {
                const dontShowAgain = data?.customCheckboxChecked ?? false;

                dontShowAgain &&
                  saveUserSettings({ skipChangedFieldDialog: true });

                this.saveSettings(true);
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                  userSettings: {
                    ...state.userSettings,
                    skipChangedFieldDialog: dontShowAgain,
                  },
                });
              }}
            />
          </Box>,
        ],
      };
    });

    this.settingsView?.on("dialog:close", async () => {
      const resourceLockPlugin =
        this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      const state = this.app?.getAppState();
      const hasUnsavedChanges = state?.editSettingsForm?.enableSaveButton;
      const hasUnpublishedChanges =
        !state?.editSettingsForm?.configuration?.isPublished;

      if (hasUnsavedChanges || hasUnpublishedChanges) {
        this.app?.patchAppState({
          showUnsavedSettingsDialog: true,
        });
        return;
      }

      this.app?.navigate(APP_ROUTES.dashboard);
      resourceLockPlugin?.unlock("settings");
      const [overlay] = await Promise.all([fetchOverlay()]);

      this.app?.patchAppState({
        overlay,
      });
    });
    this.settingsView?.on("dialog:navigate", (payload) => {
      const currentPath = payload.payload?.currentPath;
      if (!currentPath) return;
      if (window.location.pathname === currentPath) return;

      window.history.pushState(window.history.state, "", currentPath);
    });
  }

  private buildRoute() {
    if (!this.app || !this.settingsView) return;

    this.route = this.app.createRoute<"viewname">({
      path: "/settings/:viewname",
      view: this.settingsView,
    });
  }

  private buildReportingUseCaseView() {
    const reportingStructurePage = this.settingsView?.addComponent({
      component: ReportingUseCasePage,
      layoutProps: { viewname: VIEW_NAMES[9] },
    });

    reportingStructurePage?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language,
      };
    });

    reportingStructurePage?.on("update:view-configuration", async (payload) => {
      if (!payload || !this.app) return;

      const configMap = {
        audit: AUDIT_VIEW_CONFIG,
        rollout: ROLLOUT_VIEW_CONFIG,
        pmo: PMO_VIEW_CONFIG,
        maturity: MATURITY_VIEW_CONFIG,
      } as const;

      const { reportLayout } = this.app.getAppState();

      const currentDisplayTabs = reportLayout?.displayTabs ?? true;

      const { tabs } =
        configMap[payload.payload as keyof typeof configMap]["project-common"];

      this.app.patchAppState({
        dashboardLayout: {
          tabs: configMap[payload.payload as keyof typeof configMap].dashboard
            .tabs,
        },
        reportLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      await createViewConfiguration({
        tabs: configMap[payload.payload as keyof typeof configMap][
          "project-common"
        ].tabs,
        canEdit: reportLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "project-common",
      });

      await createViewConfiguration({
        tabs: configMap[payload.payload as keyof typeof configMap].dashboard
          .tabs,
        canEdit: reportLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "dashboard",
      });
    });
  }

  private buildReportingStructureView() {
    const reportingStructurePage = this.settingsView?.addComponent({
      component: ReportingStructurePage,
      layoutProps: { viewname: VIEW_NAMES[0] },
    });

    reportingStructurePage?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language,
      };
    });
  }

  private buildReportingPeriodView() {
    const reportingPeriodPage = this.settingsView?.addComponent({
      component: ReportingPeriodPage,
      layoutProps: { viewname: VIEW_NAMES[1] },
    });

    reportingPeriodPage?.mapState(({ language }) => {
      return { language: language };
    });
  }

  private buildProjectsDataModelView() {
    const projectBasicFieldsForm = this.settingsView?.addComponent({
      component: ProjectsBasicFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[2] },
    });

    projectBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language,
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }

  private buildItemsDataModelView() {
    const itemBasicFieldsForm = this.settingsView?.addComponent({
      component: ItemBasicFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[3] },
    });

    itemBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language,
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }

  private buildManageReprtingDataView() {
    const projectBasicFieldsForm = this.settingsView?.addComponent({
      component: ReportingFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[4] },
    });

    projectBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration,
        language: state.language,
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }
  private buildManageProjectsView() {
    const manageProjectsView = this.settingsView?.addComponent({
      component: ManageOEsSettingsPage,
      layoutProps: { viewname: VIEW_NAMES[5] },
    });
    manageProjectsView?.mapState((state) => {
      return {
        projects: state.settings?.projects ?? [],
        configuration: state.settings?.configuration,
        language: state.language,
      };
    });
  }
  private buildManageActivitiesView() {
    const manageSubentitiesView = this.settingsView?.addComponent({
      component: ItemManagerPage,
      layoutProps: { viewname: VIEW_NAMES[6] },
    });
    manageSubentitiesView?.mapState((state) => ({
      basicItems: state.settings?.basicItems ?? ([] as BasicItem[]),
      projects: state.settings?.projects ?? [],
      configuration: state.settings?.configuration,
      language: state.language,
    }));
  }
  private buildManageUsersView() {
    const manageAccessView = this.settingsView?.addComponent({
      component: ManageUsersPage,
      layoutProps: { viewname: VIEW_NAMES[7] },
    });
    manageAccessView?.mapState((state) => ({
      configuration: state.settings?.configuration,
      language: state.language,
      currentUserEmail: state.userProfile?.email,
      currentUserName: state.userProfile?.name,
    }));
  }

  private updateImportDialog(state: {
    excelHeaders?: string[];
    itemFields?: string[];
    open?: boolean;
    file?: File | null;
    isLoading?: boolean;
    excelData?: unknown[][];
  }) {
    const currentState = this.app?.getAppState();
    this.app?.patchAppState({
      importDialog: {
        excelHeaders: [],
        itemFields: [],
        open: false,
        file: null,
        suggestedMapping: {},
        isLoading: false,
        excelData: [],
        ...currentState?.importDialog,
        ...state,
      },
    });
  }

  private buildExcelMappingDialog() {
    const mappingDialog = this.settingsView?.addComponent({
      component: ExcelImportPopup,
      layoutProps: { viewname: VIEW_NAMES[6] },
    });

    mappingDialog?.mapState((state) => ({
      excelHeaders: state.importDialog?.excelHeaders ?? [],
      itemFields: state.importDialog?.itemFields ?? [],
      open: state.importDialog?.open ?? false,
      suggestedMapping: state.importDialog?.suggestedMapping ?? {},
      isLoading: state.importDialog?.isLoading ?? false,
      excelData: state.importDialog?.excelData ?? [],
      language: state.language,
      onClose: () => {
        this.updateImportDialog({ open: false });
      },
    }));

    mappingDialog?.on(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      "excel-import:save" as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (event: any) => {
        let { mapping = {} } = event.payload ?? {};
        const { headerRowIndex = 0, dataStartRowIndex = 1 } =
          event.payload ?? {};
        const currentState = this.app?.getAppState();
        const file = currentState?.importDialog?.file;
        if (!file) return;

        if (Object.keys(mapping).length === 0) {
          const suggestedMapping =
            currentState?.importDialog?.suggestedMapping ?? {};
          const itemFields = currentState?.importDialog?.itemFields ?? [];
          const excelHeaders = currentState?.importDialog?.excelHeaders ?? [];

          const newMapping: Record<string, string> = {};
          excelHeaders.forEach((header) => {
            const suggestedValue = suggestedMapping[header];
            if (suggestedValue && itemFields.includes(suggestedValue)) {
              newMapping[header] = suggestedValue;
            }
          });

          mapping = newMapping;
        }

        this.updateImportDialog({
          isLoading: true,
        });

        try {
          const importResult = await importItems(
            file,
            mapping,
            undefined,
            headerRowIndex,
            dataStartRowIndex
          );

          if (!importResult || typeof importResult !== "object") {
            throw new Error("Invalid import result received from server");
          }

          const currentForm = currentState.editSettingsForm;
          const existingMeasures = currentForm?.basicItems ?? [];
          const existingFindings = currentForm?.projects ?? [];

          const stagedMeasures = importResult.stagedMeasures ?? [];
          const stagedFindings = importResult.stagedFindings ?? [];

          this.app?.patchAppState({
            editSettingsForm: {
              ...currentForm,
              basicItems: [...existingMeasures, ...stagedMeasures],
              projects: [...existingFindings, ...stagedFindings],
            },
          });

          this.updateImportDialog({ open: false });
        } catch (error) {
          console.error("[ExcelImport] Import failed:", error);
          this.updateImportDialog({
            open: true,
            isLoading: false,
          });
        }
      }
    );
  }

  private setupExcelImportHandlers() {
    const handleExcelFileSelected = async (event: CustomEvent) => {
      const file = event.detail?.file as File;
      console.debug("[ExcelImport] File selected event received", {
        hasFile: !!file,
        name: file?.name,
      });
      if (!file) return;

      this.updateImportDialog({ open: true, file, isLoading: true });

      try {
        const result = await parseExcelHeaders(file);
        console.debug("[ExcelImport] Parsed headers", {
          excelHeaders: result.excelHeaders,
          itemFields: result.itemFields,
          excelDataRows: result.excelData?.length || 0,
        });
        this.updateImportDialog({
          excelHeaders: result.excelHeaders,
          itemFields: result.itemFields,
          excelData: result.excelData,
          open: true,
          file,
          isLoading: false,
        });
      } catch (error) {
        console.error("[ExcelImport] Failed to parse Excel headers:", error);
        this.updateImportDialog({ open: true, isLoading: false });
      }
    };

    document.removeEventListener(
      "excel-file-selected",
      handleExcelFileSelected as unknown as EventListener
    );
    document.addEventListener(
      "excel-file-selected",
      handleExcelFileSelected as unknown as EventListener
    );
  }
  private buildNotificationsView() {
    if (!this.settingsView || !this.route) return;

    const notificationsPlugin = this.app?.getPluginByName<
      NotificationsPlugin<TestAppState>
    >("NotificationsPlugin");

    notificationsPlugin?.renderNotificationsManager({
      view: this.settingsView,
      route: this.route as Route,
      layoutProps: { viewname: VIEW_NAMES[8] },
    });
  }

  private saveSettings = async (publish: boolean) => {
    if (!this.app) return;

    const state = this.app?.getAppState();

    const validation = validateConfiguration(
      state.editSettingsForm?.configuration ?? ({} as Configuration),
      state.editSettingsForm?.projects ?? [],
      state.editSettingsForm?.basicItems ?? [],
      state.editSettingsForm?.toolUsers ?? [],
      state.editSettingsForm?.clientUsers ?? []
    );

    // Merge and keep only concrete errors. Notification inputs clear client-side
    // errors by setting them to `undefined`; those keys must not block saving.
    const combinedErrors = Object.fromEntries(
      Object.entries({
        ...validation,
        ...(state.editSettingsForm?.validationErrors ?? {}),
      }).filter(([, error]) => Boolean(error))
    ) as Record<string, ExtendedFieldError>;

    if (Object.keys(combinedErrors ?? {}).length) {
      this.app?.patchAppState({
        editSettingsForm: {
          ...(this.app?.getAppState().editSettingsForm ??
            state.editSettingsForm),
          validationErrors: combinedErrors,
        },
      });
      return;
    }

    this.app?.patchAppState({
      editSettingsForm: {
        ...state.editSettingsForm,
        saveInProgress: true,
      },
    });
    const commit: SettingsCommit = {
      invitationLanguage: state.language,
      publish: publish,
      configuration: {
        ...state.editSettingsForm?.configuration,
        isPublished: publish,
      },
      projects: {
        upsert: (state.editSettingsForm?.projects ?? []).filter(
          (p) => p.flag !== "delete"
        ),
        delete: (state.editSettingsForm?.projects ?? [])
          .filter((p) => p.flag === "delete")
          .map((p) => p.id!)
          .filter(Boolean),
      },
      basicItems: {
        upsert: (state.editSettingsForm?.basicItems ?? []).filter(
          (b) => b.flag !== "delete"
        ),
        delete: (state.editSettingsForm?.basicItems ?? [])
          .filter((b) => b.flag === "delete")
          .map((b) => b.id!)
          .filter(Boolean),
      },
      customFieldDefinitions: {
        upsert: (
          state.editSettingsForm?.configuration?.customFields ?? []
        ).filter(
          (cf) => cf.flag !== "delete"
        ) as SettingsCommitCustomFieldUpsert[],
        delete: (state.editSettingsForm?.configuration?.customFields ?? [])
          .filter((cf) => cf.flag === "delete")
          .map((cf) => cf.id!)
          .filter(Boolean),
      },
      users: {
        toolUsers: state.editSettingsForm
          ?.toolUsers as SettingsCommitUserChange[],
        clientUsers: state.editSettingsForm
          ?.clientUsers as SettingsCommitUserChange[],
      },
      notifications: {
        upsert: state.changedNotifications ?? [],
        scheduledUpsert: state.changedScheduledNotifications ?? [],
        published: publish,
      },
    };
    const [settings] = await Promise.all([commitSettings(commit)]);

    if (!settings) {
      // The request failed (network/server/parse error) rather than returning
      // validation errors — give the user feedback instead of failing silently.
      showSnackbar(
        this.app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "settings.saveFailed"
        ) || "Saving failed. Please try again.",
        "error"
      );
      return;
    }

    if ("validationErrors" in settings) {
      this.app?.patchAppState({
        editSettingsForm: {
          ...state.editSettingsForm,
          validationErrors: settings.validationErrors as Record<
            string,
            ExtendedFieldError
          >,
        },
      });
      return;
    }
    const previousConfiguration =
      state.settings?.configuration ?? state.editSettingsForm?.configuration;
    const nextConfiguration = {
      ...(settings?.configuration ?? ({} as Configuration)),
      client: previousConfiguration?.client,
      tool: previousConfiguration?.tool,
    } as Configuration;

    showSnackbar(
      this.app as unknown as App<
        UniversalAppState & DefaultOverlayPartialAppState
      >,
      publish
        ? getLocalizedMessage(
            this.app?.getAppState().language || "en",
            "settings.savedAndPublished"
          ) || `Settings have been saved and published.`
        : getLocalizedMessage(
            this.app?.getAppState().language || "en",
            "settings.saved"
          ) || `Settings have been saved`,
      "success"
    );
    this.app?.patchAppState({
      overlay: {
        projects: settings?.projects ?? ([] as Project[]),
      },
      settings: {
        projects: settings?.projects ?? ([] as Project[]),
        basicItems: settings?.basicItems ?? ([] as BasicItem[]),
        configuration: nextConfiguration,
        mfaPolicy: settings?.mfaPolicy ?? state.settings?.mfaPolicy,
      },
      editSettingsForm: {
        projects: settings?.projects ?? ([] as Project[]),
        basicItems: settings?.basicItems ?? ([] as BasicItem[]),
        configuration: nextConfiguration,
        // Prefer the server's fresh roster; fall back to whatever the form
        // already had rather than blanking it, since a missing/empty server
        // response here must never be read as "delete everyone".
        toolUsers: (settings?.toolUsers ??
          state.editSettingsForm?.toolUsers ??
          ([] as Profile[])) as unknown as Profile[],
        clientUsers: (settings?.clientUsers ??
          state.editSettingsForm?.clientUsers ??
          ([] as Profile[])) as unknown as Profile[],
        seatInfo: settings?.seatInfo ?? state.editSettingsForm?.seatInfo,
        mfaPolicy: settings?.mfaPolicy ?? state.editSettingsForm?.mfaPolicy,
        enableSaveButton: false,
        ...(publish
          ? {
              hasCreatedFields: false,
              hasDeletedFields: false,
            }
          : {
              hasCreatedFields:
                state.editSettingsForm?.hasCreatedFields ?? false,
              hasDeletedFields:
                state.editSettingsForm?.hasDeletedFields ?? false,
            }),
      },
    });
  };

  private getModifiedFieldsDialogText(
    hasCreatedFields?: boolean,
    hasDeletedFields?: boolean
  ) {
    if (hasCreatedFields && hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.message.dataFieldCreatedAndDeleted"
        ) ||
        "You have deactivated/deleted and activated/created a data field. Any activated/created fields are now integrated into the forms and the corresponding cards are made available in the component library. Any deactivated/deleted fields are no longer integrated into the forms and corresponding cards are no longer available in the component library."
      );
    } else if (hasCreatedFields && !hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.message.dataFieldActivatedOrCreated"
        ) ||
        "You have activated a data field or created a new data field. These fields are now integrated into the forms. Corresponding cards are made available in the component library. These still need to be integrated into the desired pages."
      );
    }

    return (
      getLocalizedMessage(
        this.app?.getAppState().language || "en",
        "dialog.message.dataFieldDeactivatedOrDeleted"
      ) ||
      "You have deactivated a data field or deleted a new data field. These fields are no longer integrated into the forms. Corresponding cards are no longer available in the component library and need to be removed from pages."
    );
  }

  private getModifiedFieldsDialogTitle(
    hasCreatedFields?: boolean,
    hasDeletedFields?: boolean
  ) {
    if (hasCreatedFields && hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.title.dataFieldCreatedAndDeleted"
        ) || "Data field created/activated and delted/deactivated"
      );
    } else if (hasCreatedFields && !hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.title.dataFieldActivatedOrCreated"
        ) || "Data field activated or created"
      );
    }

    return (
      getLocalizedMessage(
        this.app?.getAppState().language || "en",
        "dialog.title.dataFieldDeactivatedOrDeleted"
      ) || "Data field deactivated or deleted"
    );
  }
}
